import React, { useState, useEffect } from 'react';
import { Box, Button, LinearProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { checkConnection } from '../../services/wahaApi';
import { useSession } from '../../contexts/SessionContext';
import { EmptyState, Panel } from '../ui/ledger';
import { useTranslation } from 'react-i18next';

// Cache session status for 30s - don't re-check on every page navigation
const statusCache: Record<string, { status: 'connected' | 'disconnected'; time: number }> = {};
const CACHE_TTL = 30000;

interface Props {
  children: React.ReactNode;
}

const WAHASessionGuard: React.FC<Props> = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeSession, loading: sessionLoading } = useSession();

  const cached = activeSession ? statusCache[activeSession] : null;
  const [status, setStatus] = useState<'loading' | 'connected' | 'disconnected'>(
    cached && (Date.now() - cached.time < CACHE_TTL) ? cached.status : 'loading'
  );

  useEffect(() => {
    if (sessionLoading || !activeSession) return;

    const cached = statusCache[activeSession];
    if (cached && (Date.now() - cached.time < CACHE_TTL)) {
      setStatus(cached.status);
      return;
    }

    const check = async () => {
      try {
        const result = await checkConnection(activeSession);
        const s = result.connected ? 'connected' : 'disconnected';
        statusCache[activeSession] = { status: s, time: Date.now() };
        setStatus(s);
      } catch {
        statusCache[activeSession] = { status: 'disconnected', time: Date.now() };
        setStatus('disconnected');
      }
    };
    check();
  }, [activeSession, sessionLoading]);

  if (sessionLoading || status === 'loading') {
    return <LinearProgress sx={{ mb: 2 }} />;
  }

  if (!activeSession) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: { xs: 2, md: 6 } }}>
        <Panel>
          <EmptyState
            title={t('WhatsApp oturumu yok')}
            description={t('Henüz bir WhatsApp oturumu oluşturulmamış. Genel Bakış sayfasından yeni bir oturum oluşturun.')}
            action={<Button variant="contained" onClick={() => navigate('/waha')}>{t("Genel Bakış'a git")}</Button>}
          />
        </Panel>
      </Box>
    );
  }

  if (status === 'disconnected') {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: { xs: 2, md: 6 } }}>
        <Panel>
          <EmptyState
            title={t('WhatsApp bağlı değil')}
            description={t('Bu sayfayı kullanabilmek için önce WhatsApp oturumunu başlatmanız ve QR kodu taratmanız gerekiyor.')}
            action={
              <Button variant="contained" onClick={() => { delete statusCache[activeSession]; navigate('/waha'); }}>
                {t('Oturumu başlat')}
              </Button>
            }
          />
        </Panel>
      </Box>
    );
  }

  return <>{children}</>;
};

export default WAHASessionGuard;
